import React, { useEffect } from 'react';
import { Message } from 'semantic-ui-react';
import {
  ATMDatePicker,
  ATMField,
  ATMGrid,
  ATMHeader,
  ATMSegment,
  ATMInput,
  ATMSelect,
  ATMMessage,
} from 'shared-it-appmod-ui';
import { get } from 'lodash';
import LabelRequired from 'src/components/atoms/label-required/label-required.component';
import Lang from 'src/libraries/language';
import Moment from 'src/libraries/moment.library';
import { getIsAdmin } from 'src/libraries/access.library';
import { IRequestFormMethods } from 'src/models/request.model';
import { getTimeOptions } from 'src/selectors/request.selector';
import styles from './flight-request-date-form.module.scss';

type IProps = IRequestFormMethods & {
  isEdit?: boolean;
  handlePast: (data: boolean) => void;
  handleDismiss: () => void;
};

const FlightRequestDateForm: React.FC<IProps> = ({
  control,
  errors,
  isEdit = false,
  handlePast,
  handleDismiss,
}) => {
  const defaultFlightDate = get(
    control,
    'defaultValuesRef.current.request.flightDate'
  );

  const minDate =
    defaultFlightDate &&
      Moment(defaultFlightDate as Date).isSameOrBefore(
        Moment().startOf('day').toDate()
      )
      ? Moment(defaultFlightDate as Date)
        .startOf('day')
        .toDate()
      : Moment().startOf('day').toDate();

  useEffect(() => {
    const elements = document.querySelectorAll('.field');
    elements.forEach((el) => {
      const element = el as HTMLElement;
      if (element.style.display && element.style.display === 'inline-block') {
        element.style.display = 'block';
        element.style.width = '100%';
      }
    });
  }, []);

  return (
    <ATMSegment>
      <ATMHeader>{Lang.TTL_REQUEST_DATE_TIME}</ATMHeader>
      <Message
        warning
        header={Lang.LBL_VERIFY_FLIGHT_DATE}
        list={[Lang.MSG_VERIFY_FLIGHT_DATE]}
        onDismiss={handleDismiss}
      />
      <ATMGrid columns={isEdit ? 3 : 2}>
        <ATMGrid.Column>
          <ATMField
            name="request.tempFlightDate"
            as={ATMInput}
            control={control}
            defaultValue={minDate}
            type="hidden"
            className="hidden"
          />
          <ATMField
            autoComplete="off"
            name="request.flightDate"
            width={12}
            label={
              <LabelRequired>
                {Lang.LBL_REQUEST_FLIGHT_START_DATE}
              </LabelRequired>
            }
            className={styles.datePicker}
            as={ATMDatePicker}
            format="MM/DD/YYYY"
            control={control}
            error={errors.request?.flightDate}
            onChange={([_, { value }]) => {
              if (
                Moment(value).isBefore(Moment(Moment().format('MM/DD/YYYY')))
              ) {
                handlePast(true);
              } else {
                handlePast(false);
              }
              return value;
            }}
            disabled={(!getIsAdmin() && isEdit)}
          />
          <ATMField
            autoComplete="off"
            name="request.flightEndDate"
            width={12}
            label={Lang.LBL_REQUEST_FLIGHT_END_DATE}
            className={styles.datePicker}
            as={ATMDatePicker}
            format="MM/DD/YYYY"
            control={control}
            error={errors.request?.flightEndDate}
            onChange={([_, { value }]) => {
              if (
                Moment(value).isBefore(Moment(Moment().format('MM/DD/YYYY')))
              ) {
                handlePast(true);
              } else {
                handlePast(false);
              }
              return value;
            }}
            disabled={(!getIsAdmin() && isEdit)}
          />
        </ATMGrid.Column>

        {isEdit && (
          <ATMGrid.Column>
            <ATMField
              autoComplete="off"
              name="request.requestDate"
              width={12}
              label={Lang.LBL_REQUEST_RECEIVED_DATE}
              className={styles.datePicker}
              as={ATMDatePicker}
              format="MM/DD/YYYY"
              control={control}
              error={errors.request?.requestDate}
              disabled={isEdit && getIsAdmin()}
            />
          </ATMGrid.Column>
        )}

        <ATMGrid.Column>
          <ATMField
            name="request.startTime"
            width={12}
            label={
              <LabelRequired>
                {Lang.LBL_REQUEST_FLIGHT_START_TIME}
              </LabelRequired>
            }
            as={ATMSelect}
            className={styles.timeSelector}
            placeholder={Lang.LBL_SELECT}
            control={control}
            error={errors.request?.startTime}
            search
            icon="clock outline"
            options={[
              ...getTimeOptions(),
              {
                key: 'night',
                value: 'night_flight',
                text: Lang.LBL_NIGHT_FLIGHT,
              },
            ]}
            onChange={([_, { value }]) => value}
            selectOnBlur={false}
            autoComplete="off"
          />
        </ATMGrid.Column>
      </ATMGrid>
      <ATMGrid columns={2}>
        <ATMGrid.Column
          style={{
            paddingTop: '0',
          }}
        >
          <ATMField
            autoComplete="off"
            name="request.flightDuration"
            width={12}
            label={
              <LabelRequired>{Lang.LBL_REQUEST_FLIGHT_DURATION}</LabelRequired>
            }
            as={ATMSelect}
            className={styles.timeSelector}
            placeholder={Lang.LBL_SELECT}
            control={control}
            error={errors.request?.flightDuration}
            search
            icon="clock outline"
            options={[
              ...Array(16)
                .fill(0)
                .map((_, i) => ({
                  key: i,
                  value: ((i + 1) * 0.5).toString(),
                  text: Lang.formatString(
                    Lang.LBL_HOURS,
                    ((i + 1) * 0.5).toFixed(1)
                  ),
                })),
              {
                key: 'more_than',
                value: 'more_than',
                text: Lang.LBL_HOUR_MORE_THAN,
              },
            ]}
            onChange={([_, { value }]) => value}
            selectOnBlur={false}
          />
        </ATMGrid.Column>
      </ATMGrid>
      <ATMGrid columns={2}>
        <ATMGrid.Column
          style={{
            paddingTop: '0',
          }}
        />
      </ATMGrid>
      <ATMMessage>{Lang.LBL_REQUEST_FLIGHT_REQUIREMENT}</ATMMessage>
    </ATMSegment>
  );
};

export default FlightRequestDateForm;
